<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { ref, computed, watch, onMounted } from "vue"
import { useI18n } from "vue-i18n"
import type { ExistingCompany } from "@core/composables"
import type { CompanyFormData } from "@core/composables"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            company: {
                title: "Organisation",
                existingCompany: "Organisation existante",
                newCompany: "Nouvelle organisation",
            },
        },
    },
})

// ================================
// TYPES & INTERFACES
// ================================
interface SelectOption {
    label: string
    value: string
}

interface Props {
    /**
     * Entreprise sélectionnée (v-model)
     */
    modelValue: ExistingCompany | null
    /**
     * Liste des entreprises existantes de l'utilisateur
     */
    companies?: ExistingCompany[]
    /**
     * Nom d'utilisateur pour le message d'info
     */
    username?: string
    /**
     * Formulaire de création d'entreprise
     */
    companyForm: CompanyFormData
    /**
     * Options de types d'organisation
     */
    companyTypeOptions?: SelectOption[]
    /**
     * Options de formes juridiques
     */
    legalFormOptions?: SelectOption[]
    /**
     * Message d'erreur pour l'identifiant
     */
    companyIdError?: string
}

interface Emits {
    /**
     * Émis lors de la sélection d'une entreprise existante
     */
    (e: "update:modelValue", value: ExistingCompany | null): void
    /**
     * Émis lors de la mise à jour du formulaire de création
     */
    (e: "update:companyForm", value: CompanyFormData): void
    /**
     * Émis lors du changement de type d'organisation
     */
    (e: "type-change"): void
}

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(defineProps<Props>(), {
    companies: () => [],
    username: "",
    companyTypeOptions: () => [],
    legalFormOptions: () => [],
    companyIdError: "",
})

const emit = defineEmits<Emits>()

// ================================
// ÉTAT
// ================================

/**
 * Mode sélection : existante ou nouvelle
 */
const mode = ref<"existing" | "new">("existing")

// ================================
// DONNÉES CALCULÉES
// ================================

/**
 * Indique si on a des entreprises existantes
 */
const getHasExistingCompanies = computed(() => props.companies.length > 0)

/**
 * Indique si on doit afficher le sélecteur d'entreprises
 */
const getShowCompanySelect = computed(() => {
    return getHasExistingCompanies.value && mode.value === "existing"
})

/**
 * Indique si on doit afficher le formulaire de création
 */
const getShowCompanyForm = computed(() => {
    return !getHasExistingCompanies.value || mode.value === "new"
})

/**
 * Indique si on doit afficher les boutons de choix (existing/new)
 */
const getShowModeSelector = computed(() => {
    return getHasExistingCompanies.value
})

// ================================
// WATCHERS
// ================================

/**
 * Initialise le mode selon la présence d'entreprises
 */
onMounted(() => {
    if (props.companies.length > 0) {
        mode.value = "existing"
    } else {
        mode.value = "new"
    }
})

/**
 * Réinitialise le mode si les entreprises changent
 */
watch(
    () => props.companies,
    () => {
        if (props.companies.length > 0) {
            mode.value = "existing"
        } else {
            mode.value = "new"
        }
    },
    { immediate: true }
)

// ================================
// INTERACTIONS
// ================================

/**
 * Gère le clic sur "Organisation existante"
 */
function onClickExisting(): void {
    mode.value = "existing"
    // Sélectionner la première entreprise si disponible
    if (props.companies.length > 0) {
        emit("update:modelValue", props.companies[0])
    }
}

/**
 * Gère le clic sur "Nouvelle organisation"
 */
function onClickNew(): void {
    mode.value = "new"
    emit("update:modelValue", null)
}

/**
 * Gère la sélection d'une entreprise existante
 * @param company - Entreprise sélectionnée
 */
function onSelectCompany(company: ExistingCompany | null): void {
    if (company) {
        mode.value = "existing"
    }
    emit("update:modelValue", company)
}

/**
 * Gère la mise à jour du formulaire de création
 * @param value - Nouvelles données
 */
function onUpdateCompanyForm(value: CompanyFormData): void {
    emit("update:companyForm", value)
}

/**
 * Gère le changement de type d'organisation
 */
function onTypeChange(): void {
    emit("type-change")
}
</script>

<template>
    <div class="owner-company-form flex flex-col gap-4 w-full">
        <!-- ================================ -->
        <!-- TITRE -->
        <!-- ================================ -->
        <h4 class="text-base font-medium text-gray-700">
            {{ t("company.title") }}
        </h4>

        <!-- ================================ -->
        <!-- CHOIX EXISTANTE / NOUVELLE -->
        <!-- ================================ -->
        <div v-if="getShowModeSelector" class="flex justify-center gap-4">
            <BaseButton
                type="button"
                :severity="mode === 'existing' ? 'primary' : 'secondary'"
                :outlined="mode !== 'existing'"
                :label="t('company.existingCompany')"
                @click="onClickExisting"
            />
            <BaseButton
                type="button"
                :severity="mode === 'new' ? 'primary' : 'secondary'"
                :outlined="mode !== 'new'"
                :label="t('company.newCompany')"
                @click="onClickNew"
            />
        </div>

        <!-- ================================ -->
        <!-- SÉLECTION D'ENTREPRISE EXISTANTE -->
        <!-- ================================ -->
        <CompanySelect
            v-if="getShowCompanySelect"
            :model-value="modelValue"
            :companies="companies"
            :username="username"
            @update:model-value="onSelectCompany"
        />

        <!-- ================================ -->
        <!-- FORMULAIRE DE CRÉATION -->
        <!-- ================================ -->
        <CompanyForm
            v-if="getShowCompanyForm"
            :model-value="companyForm"
            :company-type-options="companyTypeOptions"
            :legal-form-options="legalFormOptions"
            :company-id-error="companyIdError"
            @update:model-value="onUpdateCompanyForm"
            @type-change="onTypeChange"
        />
    </div>
</template>
